import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { filter, get, includes, isEmpty, set, sortBy } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { IMenuItem } from '@wuitk/angular/components/common/menu-items-interface';
import { Widget } from '@ispm-client/modules/widgets/decorator/widget.decorator';
import { IWidgetItem } from '@ispm-client/modules/widgets/interfaces/widget-item.interface';
import { ITimelineConfig } from '@ispm-client/modules/timeline/interfaces/timeline.interface';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { EventService } from '@ispm-client/modules/events/services/event.service';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { TimelineEventInformationComponent } from './timeline-event-information/timeline.event.information.component';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { TranslateService } from '@ngx-translate/core';
import * as immutable from 'immutable';
import { IEventDefinition } from '@ispm-client/modules/events/Interfaces';

import 'rxjs/add/observable/combineLatest';
import {TimelineService} from "@ispm-client/modules/timeline/services/timeline.service";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

type ITimelineMenuItem = IMenuItem & {
  eventConfig: any,
  eventData: any
};

const TODAY_TRANSLATION = 'timeline_event_today';

@Component({
  selector: 'ispm-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('Timeline')
export class TimelineComponent implements OnInit, IWidgetItem, OnDestroy {
  widgetUpdateValueCallback: (value) => void;
  public config: ITimelineConfig;
  public eventMappedData;
  public panel: ElementRef;
  private timelineData;
  public timelineLoaded: boolean;
  public totalEvents;
  private eventDataSubscription: Subscription;
  public hasScrollbar: boolean = false;
  public events: ITimelineMenuItem[] = [];
  public eventTypes = [];
  @Input()
  patientId;

  private static getEventDefinition(eventDefinitions: immutable.Map<string, IEventDefinition>, category: string): IEventDefinition {
    // get event definition
    if (!isEmpty(eventDefinitions)) {
      const def = eventDefinitions.get(category);

      return (def) ? def : eventDefinitions.get('default');
    }
    return null;
  }

  @ViewChild('timelinePanel', { static: false, read: ElementRef })
  public set _panel(_panel: ElementRef) {
    this.panel = _panel;
    if (this.panel) {
      this.hasScrollbar = this.panel.nativeElement.scrollWidth > this.panel.nativeElement.clientWidth;
      this.changeDetector.detectChanges();
    }
  }

  constructor(
    private routerManagerService: RouterManagerService,
    private changeDetector: ChangeDetectorRef,
    private _eventService: EventService,
    private datePipe: DatePipe,
    private _modalLoader: DynamicModalLoaderService,
    private _ngZone: NgZone,
    private translateService: TranslateService,
    private timelineService: TimelineService
  ) { }

  ngOnInit() {
    this._eventService.getAllEventsAndStore().then(resp => {
      if (!this.patientId) {
        this.patientId = this.routerManagerService.parameters.patientId;
      }
      this.eventDataSubscription = Observable.combineLatest(this._eventService.getAllEvents(), this._eventService.getAllEventDefinition()).subscribe(data => {
        let events: any;
        const eventDefinitions: any = data[1];
        events = data[0].toArray();
        if ((!isEmpty(eventDefinitions) && eventDefinitions.size > 0)) {
          this.timelineData = events;
          this.totalEvents = events.length;
          this.eventMappedData = this.getMappedTimelineDataWithConfig(eventDefinitions, get(this.config, 'queryParams'));
          this.changeDetector.markForCheck();
          this.timelineLoaded = true;
          this.events = this.eventMappedData.map(item => this.convertToMenuItem(item));
          this.setEventTypes(eventDefinitions);
          setTimeout(() => {
            if (this.panel) {
              this.panel.nativeElement.scrollLeft = this.panel.nativeElement.scrollWidth;
            }
          }, 0);
        }
        this.timelineLoaded = true;
        this.changeDetector.markForCheck();
      });
    });
  }

  /**
   * set patient bar widget config
   * @param _itemConfig
   */
  set widgetConfig(_itemConfig: IdynamicFormItem) {
    this.config = _itemConfig.widgetPayload;
  }

  /**
   * set patient bar widget config
   * @param widgetData
   */
  set widgetData(widgetData: any) {
    this.eventMappedData = widgetData;
  }

  private setEventTypes(eventDefinitions: immutable.Map<string, IEventDefinition>) {
    this.eventTypes = this.eventMappedData
      .filter(event => !!event.data.category)
      .reduce((list, event) => {
        const category = event.data.category.code;
        if (list.find(item => item.code === category)) {
          return list;
        }
        let eventDefinition: IEventDefinition;
        setTimeout(() => {
          eventDefinition = TimelineComponent.getEventDefinition(eventDefinitions, category);
        }, 0)
        const group = get(eventDefinition, 'ui.group.display');
        return [...list, {
          ...event.data.category,
          group
        }];
      }, [])
      .sort((a, b) => {
        if (a.group && b.group) {
          const grpCmp = a.group.localeCompare(b.group);
          if (grpCmp !== 0) { return grpCmp; }
          return a.display.localeCompare(b.display);
        }
        if (a.group) { return 1; }
        if (b.group) { return -1; }
        return a.display.localeCompare(b.display);
      });
  }

  private convertToMenuItem(item) {
    const timelineConfig = get(item, 'config.ui.timeline');
    const timestamp = this.datePipe.transform(item.data.date, timelineConfig.dateFormat,undefined,this.translateService.currentLang);
    if (timelineConfig &&
      timelineConfig.category === 'year') {
      return {
        type: 'header',
        timestamp,
        status: 'completed'
      } as ITimelineMenuItem;
    }
    const isToday = (moment(item.data.date).isSame(Date.now(), 'day'));

    const eventItem: ITimelineMenuItem = {
      type: 'event',
      title: item.data.category ? this.translateService.instant(item.data.category.display) : '',
      timestamp: isToday ? this.translateService.instant(TODAY_TRANSLATION) : timestamp,
      status: isToday ? 'current' : 'completed',
      icon: timelineConfig.markerIcon,
      isCustomIcon: false,
      eventData: item.data,
      eventConfig: item.config
    };

    if (!item.data.approved) {
      eventItem.badge = '';
      eventItem.badgeSize = 'dot';
      eventItem.badgeColor = 'warning';
    }
    return eventItem;
  }

  public filterEvents(categories: { code: string; display: string; }[]) {
    if (categories) {
      const codes = categories.map(category => category.code);
      this.events = this.eventMappedData
        .filter(item => {
          const categoryCode = get(item, 'data.category.code');
          return !categoryCode ||
            codes.length < 1 ||
            codes.indexOf(categoryCode) >= 0;
        })
        .map(item => this.convertToMenuItem(item));
      setTimeout(() => this.onResizeTimeline(), 0);
    }
  }


  public onEventSelect(event: ITimelineMenuItem) {
    const timelineConfig = get(event, 'eventConfig.ui.timeline');
    if (!timelineConfig) { return; }
    this.timelineService.setTimelineSelectedEvent(event.eventData);
    if (!timelineConfig.tooltipShow) {
      return;
    } else {
      this._modalLoader.popupModal(PlaceholderModalComponent, PlaceholderModalLoaderPayload.builder()
        .setComponentToLoad(TimelineEventInformationComponent)
        .setTitle(event.eventData.category.display)
        .setPayload({ data: event.eventData, config: event.eventConfig }), ModalTypes.CUSTOM);
    }
  }

  /**
   * Scroll backward
   */
  scrollBackward(): void {
    this._ngZone.runOutsideAngular(() => {
      this.sideScroll(
        this.panel.nativeElement,
        'left',
        10,
        this.panel.nativeElement.offsetWidth,
        25
      );
    });
  }

  /**
   * Scroll forward
   */
  scrollForward(): void {
    this._ngZone.runOutsideAngular(() => {
      this.sideScroll(
        this.panel.nativeElement,
        'right',
        10,
        this.panel.nativeElement.offsetWidth,
        25
      );
    });
  }

  /**
   * Scroll smooth by frame
   * @param element
   * @param direction
   * @param speed
   * @param distance
   * @param step
   */
  public sideScroll(element, direction, speed, distance, step) {
    let scrollAmount = 0;
    const slideTimer = setInterval(function () {
      if (direction === 'left') {
        element.scrollLeft -= step;
      } else {
        element.scrollLeft += step;
      }
      scrollAmount += step;
      if (scrollAmount >= distance) {
        clearInterval(slideTimer);
      }
    }, speed);
  }

  /**
   * on Resize Timeline
   */
  onResizeTimeline() {
    this.hasScrollbar = this.panel.nativeElement.scrollWidth > this.panel.nativeElement.clientWidth;
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {

    // it may be that subscriber had not been initialized yet - see: GEN-78220
    if (this.eventDataSubscription) {
      this.eventDataSubscription.unsubscribe();
    }
  }


  /**
   * Create event object with data and config
   * @param params - query params to get events and configurations
   * @return [{data: ITimelineEventData, config: ITimelineEventConfig}]
   */
  getMappedTimelineDataWithConfig(eventDefinitions?: any, params?: any) {
    const eventMappedData = [];
    let prevYear;
    // filter based on configured event categories and includeInTimeline flag
    this.timelineData = filter(this.timelineData, (item => {
      const isConfiguredEventCategory = get(params, 'category') ? includes(get(params, 'category'), get(item, 'category.code')) : true;
      const shouldFilterNotApproved = get(params, 'showOnlyApproved') ? get(item, 'approved') : true;

      return (
        isConfiguredEventCategory &&
        get(item, 'includeInTimeline') &&
        shouldFilterNotApproved &&
        !get(item, 'archived')
      );
    }));
    // sort by date
    this.timelineData = sortBy(this.timelineData, ['date']);

    for (const timelineEvent of this.timelineData) {
      const currentYear = new Date(timelineEvent.date).getFullYear();
      let eventObj = {};

      // create year event to display on timeline
      if (prevYear !== currentYear) {
        set(eventObj, 'data', { date: get(timelineEvent, 'date') });
        set(eventObj, 'config', this.getEventDefinition(eventDefinitions, 'year'));
        eventMappedData.push(eventObj);
        prevYear = currentYear;
      }

      eventObj = {};
      set(eventObj, 'data', timelineEvent);
      set(eventObj, 'config', this.getEventDefinition(eventDefinitions, get(timelineEvent, 'category.code')));
      eventMappedData.push(eventObj);
    }
    return eventMappedData;
  }

  getEventDefinition(eventDefinitions, category: string) {
    // get event definition
    if (!isEmpty(eventDefinitions)) {
      const def = eventDefinitions.get(category);

      return (def) ? def : eventDefinitions.get('default');
    }
    return null;
  }

}
